import { useState, useEffect } from 'react';
import { getIngestUrl, getApiUrl, authFetch } from '../config';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import SubPageHeader from '../components/SubPageHeader';

const API_BASE = getIngestUrl('/blocklist');

export default function BlocklistOverview() {
  const [sources, setSources] = useState<any[]>([]);
  const [blockedPlayers, setBlockedPlayers] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'sources' | 'players'>('sources');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'sources') {
        await loadSources();
      } else {
        await loadBlockedPlayers();
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSources = async () => {
    const response = await authFetch(`${API_BASE}/sources`);
    const data = await response.json();
    setSources(Array.isArray(data.sources) ? data.sources : []);
  };

  const loadBlockedPlayers = async () => {
    const response = await authFetch(getApiUrl('/players?blocked=true'));
    const data = await response.json();
    const players = Array.isArray(data) && Array.isArray(data[0]) ? data[0] : data;
    setBlockedPlayers(Array.isArray(players) ? players : []);
  };

  const handleUnblock = async (playerId: number, playerName: string) => {
    if (!window.confirm(`Spieler "${playerName}" entsperren?`)) return;

    try {
      const response = await authFetch(`${API_BASE}/unblock/${playerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Manuell entsperrt durch Admin' })
      });

      const data = await response.json();
      if (data.success) {
        alert('Spieler erfolgreich entsperrt');
        loadBlockedPlayers();
      }
    } catch (error) {
      alert('Fehler beim Entsperren: ' + (error as Error).message);
    }
  };

  const getBlocklistTypeColor = (type: string) => {
    switch (type) {
      case 'robinson': return '#dc2626';
      case 'widerspruch': return '#ea580c';
      case 'custom': return '#9333ea';
      default: return '#6b7280';
    }
  };

  const getBlocklistTypeLabel = (type: string) => {
    switch (type) {
      case 'robinson': return 'Robinson-Liste';
      case 'widerspruch': return 'Widerspruch';
      case 'custom': return 'Benutzerdefiniert';
      default: return type;
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <SubPageHeader title="Sperrlisten-Verwaltung" />

      <main style={{ maxWidth: '1400px', margin: '2rem auto', padding: '0 2rem' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ margin: '0 0 0.5rem 0', color: '#111827', fontSize: '1.875rem', fontWeight: '600' }}>
              Sperrlisten-Verwaltung
            </h1>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.95rem' }}>
              Verwalten Sie gesperrte Spieler und Sperrlisten-Quellen
            </p>
          </div>
          <a
            href="/blocklist-import"
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#dc2626',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              fontWeight: '500',
              display: 'inline-block'
            }}
          >
            + Neue Sperrliste importieren
          </a>
        </div>

        {/* Tabs */}
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '8px 8px 0 0', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          border: '1px solid #e5e7eb',
          borderBottom: 'none'
        }}>
          <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
            <button
              onClick={() => setActiveTab('sources')}
              style={{
                flex: 1,
                padding: '1rem',
                border: 'none',
                backgroundColor: activeTab === 'sources' ? 'white' : '#f9fafb',
                borderBottom: activeTab === 'sources' ? '2px solid #dc2626' : '2px solid transparent',
                fontWeight: activeTab === 'sources' ? '600' : '400',
                color: activeTab === 'sources' ? '#dc2626' : '#6b7280',
                cursor: 'pointer',
                fontSize: '0.95rem'
              }}
            >
              📋 Sperrlisten-Quellen ({sources.length})
            </button>
            <button
              onClick={() => setActiveTab('players')}
              style={{
                flex: 1,
                padding: '1rem',
                border: 'none',
                backgroundColor: activeTab === 'players' ? 'white' : '#f9fafb',
                borderBottom: activeTab === 'players' ? '2px solid #dc2626' : '2px solid transparent',
                fontWeight: activeTab === 'players' ? '600' : '400',
                color: activeTab === 'players' ? '#dc2626' : '#6b7280',
                cursor: 'pointer',
                fontSize: '0.95rem'
              }}
            >
              🚫 Gesperrte Spieler ({blockedPlayers.length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0 0 8px 8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          border: '1px solid #e5e7eb',
          borderTop: 'none'
        }}>
          {loading ? (
            <div style={{ padding: '4rem', textAlign: 'center' }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #e5e7eb',
                borderTopColor: '#dc2626',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
            </div>
          ) : (
            <>
              {/* Sources Tab */}
              {activeTab === 'sources' && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      <tr>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Quelle
                        </th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Typ
                        </th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Upload-Datum
                        </th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Gesperrte Spieler
                        </th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sources.length === 0 ? (
                        <tr>
                          <td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                            Keine Sperrlisten vorhanden
                          </td>
                        </tr>
                      ) : (
                        sources.map((source) => {
                          let blocklistType = 'custom';
                          try {
                            if (source.attributes) {
                              const attrs = typeof source.attributes === 'string' 
                                ? JSON.parse(source.attributes) 
                                : source.attributes;
                              blocklistType = attrs.blocklist_type || 'custom';
                            }
                          } catch (e) {}

                          return (
                            <tr key={source.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '0.75rem 1rem' }}>
                                <div style={{ fontWeight: '500', color: '#111827' }}>{source.name}</div>
                                {source.description && (
                                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                    {source.description}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '0.75rem 1rem' }}>
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  fontSize: '0.75rem',
                                  borderRadius: '9999px',
                                  fontWeight: '500',
                                  backgroundColor: `${getBlocklistTypeColor(blocklistType)}20`,
                                  color: getBlocklistTypeColor(blocklistType)
                                }}>
                                  {getBlocklistTypeLabel(blocklistType)}
                                </span>
                              </td>
                              <td style={{ padding: '0.75rem 1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                                {formatDateTime(source.upload_date)}
                              </td>
                              <td style={{ padding: '0.75rem 1rem' }}>
                                <span style={{ fontWeight: '600', color: '#dc2626', fontSize: '1.125rem' }}>
                                  {source.blocked_players || 0}
                                </span>
                              </td>
                              <td style={{ padding: '0.75rem 1rem' }}>
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  fontSize: '0.75rem',
                                  borderRadius: '9999px',
                                  fontWeight: '500',
                                  backgroundColor: source.status === 'completed' ? '#d1fae5' : '#fef3c7',
                                  color: source.status === 'completed' ? '#065f46' : '#92400e'
                                }}>
                                  {source.status === 'completed' ? 'Abgeschlossen' : source.status}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Players Tab */}
              {activeTab === 'players' && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      <tr>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Spieler
                        </th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Adresse
                        </th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Sperrgrund
                        </th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Gesperrt seit
                        </th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                          Aktionen
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {blockedPlayers.length === 0 ? (
                        <tr>
                          <td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                            Keine gesperrten Spieler vorhanden
                          </td>
                        </tr>
                      ) : (
                        blockedPlayers.map((player) => (
                          <tr key={player.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <div style={{ fontWeight: '500', color: '#111827' }}>
                                {player.vorname} {player.nachname}
                              </div>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                Kunden-Nr: {player.kunden_nr}
                              </div>
                            </td>
                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                              <div>{player.strasse}</div>
                              <div>{player.plz} {player.ort}</div>
                            </td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <span style={{
                                padding: '0.25rem 0.75rem',
                                fontSize: '0.75rem',
                                borderRadius: '9999px',
                                fontWeight: '500',
                                backgroundColor: '#fee2e2',
                                color: '#991b1b'
                              }}>
                                {player.blocked_reason || 'Gesperrt'}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                              {formatDate(player.blocked_at)}
                            </td>
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                              <button
                                onClick={() => handleUnblock(player.id, `${player.vorname} ${player.nachname}`)}
                                style={{
                                  padding: '0.5rem 1rem',
                                  backgroundColor: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                  cursor: 'pointer'
                                }}
                              >
                                Entsperren
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}